import React, {useRef} from 'react'
import styles from './skip-to-main-content.module.css'

interface SkipToMainContentProps {
	text?: string
	mainContentId?: string
}

export const SkipToMainContent: React.FC<SkipToMainContentProps> = ({
	mainContentId = 'main-content',
	text = 'Skip to main content',
}) => {
	const skipLinkRef = useRef<HTMLAnchorElement>(null)

	const handleFocus = (): void => {
		// Show the skip link when it receives focus
		if (skipLinkRef.current) {
			skipLinkRef.current.style.position = 'static'
			skipLinkRef.current.style.left = 'auto'
		}
	}

	const handleBlur = (): void => {
		// Hide the skip link when it loses focus
		if (skipLinkRef.current) {
			skipLinkRef.current.style.position = 'absolute'
			skipLinkRef.current.style.left = '-9999px'
		}
	}

	const handleKeyDown = (event: React.KeyboardEvent): void => {
		if (event.key === 'Enter') {
			event.preventDefault()
			const mainContent = document.getElementById(mainContentId)
			if (mainContent) {
				// Make main content focusable if it's not already
				if (!mainContent.hasAttribute('tabindex')) {
					mainContent.setAttribute('tabindex', '-1')
				}
				mainContent.focus()
				mainContent.scrollIntoView()
			}
			// Hide the skip link after navigation
			if (skipLinkRef.current) {
				skipLinkRef.current.style.position = 'absolute'
				skipLinkRef.current.style.left = '-9999px'
			}
		} else if (event.key === 'Tab') {
			// Hide the skip link when tabbing away
			if (skipLinkRef.current) {
				skipLinkRef.current.style.position = 'absolute'
				skipLinkRef.current.style.left = '-9999px'
			}
		}
	}

	const handleClick = (event: React.MouseEvent): void => {
		event.preventDefault()
		const mainContent = document.getElementById(mainContentId)
		if (mainContent) {
			// Make main content focusable if it's not already
			if (!mainContent.hasAttribute('tabindex')) {
				mainContent.setAttribute('tabindex', '-1')
			}
			mainContent.focus()
			mainContent.scrollIntoView()
		}
		// Hide the skip link after navigation
		if (skipLinkRef.current) {
			skipLinkRef.current.style.position = 'absolute'
			skipLinkRef.current.style.left = '-9999px'
		}
	}

	return (
		<a
			aria-label={text}
			className={styles.skipToMainContent}
			href={`#${mainContentId}`}
			onBlur={handleBlur}
			onClick={handleClick}
			onFocus={handleFocus}
			onKeyDown={handleKeyDown}
			ref={skipLinkRef}
			style={{position: 'absolute', left: '-9999px'}}
			tabIndex={0}
		>
			{text}
		</a>
	)
}

// Component is exported using named export above
